import React, { useEffect, useState } from 'react';
import { Button, Modal, Space, Typography, message } from 'antd';
import legalService from '../../services/legal.service';
import type { LegalConsentItem, LegalDocumentSummary } from '../../constants/legalDocuments';
import { useLegalDocumentViewer } from './LegalDocumentViewer';

type LegalConsentModalProps = {
  open: boolean;
  documents: LegalDocumentSummary[];
  onAccepted: () => void;
  onCancel: () => void;
};

const LegalConsentModal: React.FC<LegalConsentModalProps> = ({
  open,
  documents,
  onAccepted,
  onCancel,
}) => {
  const { openDocument } = useLegalDocumentViewer();
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (open) setChecked(false);
  }, [open, documents]);

  const handleAccept = async () => {
    if (!checked) {
      message.warning('请先勾选同意协议');
      return;
    }
    const consents: LegalConsentItem[] = legalService.buildConsentPayload(documents);
    try {
      setSubmitting(true);
      await legalService.acceptConsents(consents, 'login');
      message.success('协议确认成功');
      onAccepted();
    } catch (e: any) {
      message.error(e?.response?.data?.message || '协议确认失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="协议更新确认"
      open={open}
      closable={false}
      mask={{ closable: false }}
      keyboard={false}
      zIndex={1500}
      footer={
        <Space>
          <Button onClick={onCancel}>退出登录</Button>
          <Button type="primary" loading={submitting} disabled={!checked} onClick={() => void handleAccept()}>
            同意并继续
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">
        平台协议已更新或您尚未完成确认。请阅读以下文档并同意后继续使用：
      </Typography.Paragraph>
      <ul style={{ paddingLeft: 20, marginBottom: 16 }}>
        {documents.map((doc) => (
          <li key={doc.slug} style={{ marginBottom: 8 }}>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => openDocument(doc.slug)}
            >
              《{doc.title}》（{doc.version}）
            </Button>
            {doc.summary ? (
              <Typography.Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 12 }}>
                {doc.summary}
              </Typography.Paragraph>
            ) : null}
          </li>
        ))}
      </ul>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          style={{ marginTop: 4 }}
        />
        <span>我已阅读并同意上述协议、隐私政策及免责声明</span>
      </label>
    </Modal>
  );
};

export default LegalConsentModal;
